<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import Modal from '@/components/ui/Modal.vue'
import { useSwitchStore } from '@/stores/switch'
import { useUserStore } from '@/stores/user'
import { extractError } from '@/api/client'
import type { SwitchServerOption } from '@/types'
import { formatCooldown, parseNextAllowedAt } from '@/utils/format'

const props = defineProps<{
  open: boolean
  keyId: number
}>()

const emit = defineEmits<{ close: [] }>()

const switchStore = useSwitchStore()
const userStore = useUserStore()

const loading = ref(false)
const status = ref('')
const error = ref('')
const options = ref<SwitchServerOption[]>([])
const selectedIdx = ref(0)
const remaining = ref(0)
const cooldownMs = ref<number | null>(null)
const cooldownLabel = ref('')
const submitting = ref(false)
const warning = ref<string | null>(null)
let cooldownTimer: ReturnType<typeof setTimeout> | null = null

const canSubmit = computed(() =>
  !loading.value
  && !submitting.value
  && options.value.length > 0
  && (cooldownMs.value === null || cooldownMs.value <= 0)
  && remaining.value > 0,
)

const selectedOption = computed(() => options.value[selectedIdx.value] ?? null)
const selectedProtocolsText = computed(() => selectedOption.value?.server.protocols_text ?? '')

function optionLabel(opt: SwitchServerOption): string {
  const base = `${opt.server.name} (${opt.server.location})`
  return opt.server.protocols_text ? `${base} — ${opt.server.protocols_text}` : base
}

function clearCooldownTimer() {
  if (cooldownTimer) {
    clearTimeout(cooldownTimer)
    cooldownTimer = null
  }
}

function tickCooldown(nextMs: number) {
  clearCooldownTimer()
  const tick = () => {
    const diff = nextMs - Date.now()
    if (diff <= 0) {
      cooldownMs.value = null
      cooldownLabel.value = ''
      status.value = 'Выберите сервер и подтвердите смену.'
      return
    }
    cooldownMs.value = diff
    cooldownLabel.value = formatCooldown(diff)
    cooldownTimer = setTimeout(tick, 1000)
  }
  tick()
}

async function loadOptions() {
  if (!props.keyId) return
  loading.value = true
  error.value = ''
  status.value = 'Загрузка…'
  options.value = []
  selectedIdx.value = 0
  warning.value = null
  clearCooldownTimer()

  try {
    await userStore.fetch(true)
    const data = await switchStore.fetchOptions(props.keyId)
    if (!data.success) {
      error.value = data.message || 'Ошибка загрузки'
      status.value = ''
      return
    }
    if (data.has_active_test) {
      switchStore.setActiveFromOptions(props.keyId, data)
      emit('close')
      return
    }
    remaining.value = data.remaining_switches ?? userStore.profile?.key_switches_remaining ?? 0
    options.value = data.options ?? []
    warning.value = data.current_server_warning ?? null

    const nextMs = parseNextAllowedAt(data.next_allowed_at)
    if (remaining.value <= 0) {
      status.value = 'Лимит смен исчерпан.'
    } else if (nextMs && Date.now() < nextMs) {
      status.value = 'Действует период ожидания после последней смены по этому ключу.'
      tickCooldown(nextMs)
    } else {
      status.value = 'Выберите сервер и подтвердите смену.'
    }
    if (options.value.length === 0) {
      status.value += ' Нет доступных серверов для смены.'
    }
  } catch (e) {
    error.value = extractError(e, 'Ошибка сети')
    status.value = ''
  } finally {
    loading.value = false
  }
}

async function submit() {
  const opt = options.value[selectedIdx.value]
  const serverId = opt?.server?.id
  if (!serverId || !props.keyId) return
  submitting.value = true
  error.value = ''
  try {
    const data = await switchStore.startSwitch(props.keyId, serverId)
    if (!data.success) {
      error.value = data.message || 'Ошибка смены сервера'
      const nextMs = parseNextAllowedAt(data.next_allowed_at)
      if (nextMs && Date.now() < nextMs) tickCooldown(nextMs)
      return
    }
    emit('close')
  } catch (e) {
    error.value = extractError(e, 'Ошибка сети')
  } finally {
    submitting.value = false
  }
}

watch(
  () => [props.open, props.keyId] as const,
  ([open]) => {
    if (open) void loadOptions()
    else clearCooldownTimer()
  },
)
</script>

<template>
  <Modal :open="open" title="Сменить сервер" max-width="md" @close="emit('close')">
    <div class="space-y-4 text-sm">
      <div class="alert alert-warning">
        После смены — 60 минут на тест. При отмене новый сервер будет заблокирован.
      </div>

      <div v-if="userStore.profile" class="rounded-md border border-white-light bg-black/[0.02] px-3 py-2">
        <span class="text-muted">Осталось смен:</span>
        <strong class="ml-2 text-primary">{{ remaining }}</strong>
        <span v-if="cooldownLabel" class="ml-3 text-warning">
          через {{ cooldownLabel }}
        </span>
      </div>

      <div v-if="warning" class="alert alert-warning">{{ warning }}</div>
      <div v-if="status && !error" class="text-muted">{{ status }}</div>
      <div v-if="error" class="alert alert-danger">{{ error }}</div>

      <div v-if="options.length">
        <label for="switch-server" class="text-sm font-semibold">Сервер</label>
        <select id="switch-server" v-model="selectedIdx" class="form-select mt-1">
          <option v-for="(opt, idx) in options" :key="opt.server.id" :value="idx">
            {{ optionLabel(opt) }}
          </option>
        </select>
        <p v-if="selectedProtocolsText" class="mt-1.5 text-xs text-muted">
          Протоколы: <span class="font-semibold">{{ selectedProtocolsText }}</span>
        </p>
      </div>
    </div>

    <template #footer>
      <button type="button" class="btn btn-outline-danger" @click="emit('close')">
        Отмена
      </button>
      <button
        type="button"
        class="btn btn-primary"
        :disabled="!canSubmit"
        @click="submit"
      >
        {{ submitting ? 'Смена…' : 'Сменить сервер' }}
      </button>
    </template>
  </Modal>
</template>
